import { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useLang } from '../i18n';

const COURSES = [
  'Clarinetto, Corno di Bassetto, Musica da Camera — Lorenzo Paini (6 incontri, nov–giu)',
  'Clarinetto, Clarinetto Piccolo, Musica da Camera — Maurizio Trapletti (6 incontri, dic–giu)',
  'Clarinetto, Musica da Camera — Angelo De Angelis (4–5 gennaio 2027)',
  'Clarinetto, Clarinetto Basso, Musica da Camera — Giuseppe Gentile (9–10 gennaio 2027)',
  'Violino, Musica da Camera — Mara Sistino (3 incontri, mar–giu)',
  'Tecnica vocale e interpretazione operistica — Matteo Sartini (9–11 aprile 2027)',
];

const inputCls =
  'w-full rounded-xl border border-[#d4af37]/20 bg-[#070e12]/70 px-4 py-3.5 text-sm text-[#f4f1ea] placeholder-[#a3b1b6]/50 outline-none transition-colors duration-300 focus:border-[#d4af37]/60';

const initial = {
  nome: '',
  email: '',
  telefono: '',
  masterclass: COURSES[0],
  ruolo: 'allievo',
  messaggio: '',
  privacy: false,
};

const EnrollmentForm = () => {
  const { t } = useLang();
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) =>
    setForm((f) => ({
      ...f,
      [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome.trim() || !form.email.trim() || !form.privacy) {
      toast.error(t('form.required'));
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/iscrizioni`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(form),
        }
      );
      if (!res.ok) throw new Error();
      toast.success(t('form.success'));
      setForm(initial);
    } catch {
      toast.error(t('form.error'));
    }
    setBusy(false);
  };

  return (
    <section
      id="iscrizione"
      data-testid="enrollment-section"
      className="relative py-24 sm:py-32"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 lg:gap-20 items-start">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="font-mono-alt text-[10px] sm:text-xs tracking-[0.35em] uppercase text-[#e6c687] mb-4">
            {t('form.overline')}
          </p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#f3e5ab] tracking-tight">
            {t('form.title.a')}{' '}
            <span className="italic text-[#d4af37]">{t('form.title.b')}</span>
          </h2>
          <p className="mt-6 max-w-md text-sm sm:text-base text-[#a3b1b6] leading-relaxed">
            {t('form.desc')}
          </p>
          <div className="hairline-gold mt-10 mb-6 max-w-md" />
          <p className="font-mono-alt text-[10px] tracking-[0.25em] uppercase text-[#a3b1b6]/70 leading-loose">
            info@trinitymusicalschool.net
            <br />
            0425 166 6483 · 391 308 4761
          </p>
        </motion.div>

        <motion.form
          data-testid="enrollment-form"
          onSubmit={submit}
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="relative rounded-2xl border border-[#d4af37]/20 bg-[#0c171c]/70 backdrop-blur-xl p-7 sm:p-10 space-y-5"
        >
          <div>
            <label htmlFor="form-name" className="block font-mono-alt text-[10px] tracking-[0.25em] uppercase text-[#e6c687] mb-2">
              {t('form.name')} *
            </label>
            <input
              id="form-name"
              data-testid="form-name-input"
              value={form.nome}
              onChange={set('nome')}
              className={inputCls}
              placeholder="Maria Rossi"
              autoComplete="name"
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label htmlFor="form-email" className="block font-mono-alt text-[10px] tracking-[0.25em] uppercase text-[#e6c687] mb-2">
                {t('form.email')} *
              </label>
              <input
                id="form-email"
                data-testid="form-email-input"
                type="email"
                value={form.email}
                onChange={set('email')}
                className={inputCls}
                placeholder="nome@email.it"
                autoComplete="email"
              />
            </div>
            <div>
              <label htmlFor="form-phone" className="block font-mono-alt text-[10px] tracking-[0.25em] uppercase text-[#e6c687] mb-2">
                {t('form.phone')}
              </label>
              <input
                id="form-phone"
                data-testid="form-phone-input"
                type="tel"
                value={form.telefono}
                onChange={set('telefono')}
                className={inputCls}
                placeholder="333 000 0000"
                autoComplete="tel"
              />
            </div>
          </div>

          <div>
            <label htmlFor="form-course" className="block font-mono-alt text-[10px] tracking-[0.25em] uppercase text-[#e6c687] mb-2">
              {t('form.course')} *
            </label>
            <select
              id="form-course"
              data-testid="form-course-select"
              value={form.masterclass}
              onChange={set('masterclass')}
              className={`${inputCls} appearance-none cursor-pointer`}
            >
              {COURSES.map((c) => (
                <option key={c} value={c} className="bg-[#0c171c]">
                  {c}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="form-role" className="block font-mono-alt text-[10px] tracking-[0.25em] uppercase text-[#e6c687] mb-2">
              {t('form.role')}
            </label>
            <select
              id="form-role"
              data-testid="form-role-select"
              value={form.ruolo}
              onChange={set('ruolo')}
              className={`${inputCls} appearance-none cursor-pointer`}
            >
              <option value="allievo" className="bg-[#0c171c]">
                {t('form.role.student')}
              </option>
              <option value="uditore" className="bg-[#0c171c]">
                {t('form.role.auditor')}
              </option>
            </select>
          </div>

          <div>
            <label htmlFor="form-message" className="block font-mono-alt text-[10px] tracking-[0.25em] uppercase text-[#e6c687] mb-2">
              {t('form.message')}
            </label>
            <textarea
              id="form-message"
              data-testid="form-message-input"
              value={form.messaggio}
              onChange={set('messaggio')}
              rows={3}
              className={`${inputCls} resize-none`}
            />
          </div>

          <label
            htmlFor="form-privacy"
            className="flex items-start gap-3 cursor-pointer select-none"
          >
            <input
              id="form-privacy"
              data-testid="form-privacy-checkbox"
              type="checkbox"
              checked={form.privacy}
              onChange={set('privacy')}
              className="mt-0.5 w-4 h-4 accent-[#d4af37] cursor-pointer"
            />
            <span className="text-xs text-[#a3b1b6] leading-relaxed">
              {t('form.privacy')} *
            </span>
          </label>

          <button
            data-testid="form-submit-btn"
            type="submit"
            disabled={busy}
            className="w-full inline-flex items-center justify-center gap-3 rounded-full bg-[#d4af37] px-8 py-4 font-mono-alt text-[11px] tracking-[0.2em] uppercase text-[#070e12] font-medium transition-all duration-300 hover:bg-[#f3e5ab] hover:shadow-[0_0_40px_-8px_rgba(212,175,55,0.6)] disabled:opacity-60"
          >
            {busy ? (
              <Loader2 size={16} className="animate-spin" strokeWidth={1.8} />
            ) : (
              <Send size={15} strokeWidth={1.8} />
            )}
            {busy ? t('form.sending') : t('form.submit')}
          </button>
        </motion.form>
      </div>
    </section>
  );
};

export default EnrollmentForm;
